import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Box,
  IconButton,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  ChevronLeft,
} from '@mui/icons-material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { navigationConfig, NavGroup } from '../config/navigation';
import { useNavigationContext } from '../hooks/useNavigationContext';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  variant?: 'permanent' | 'temporary';
}

export const Sidebar = ({ open, onClose, variant = 'permanent' }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentReceipt } = useNavigationContext();
  const [expandedGroups, setExpandedGroups] = useState<string[]>(() =>
    navigationConfig.map((group) => group.id)
  );

  const drawerWidth = 280;

  const handleNavigate = (path: string) => {
    navigate(path);
    if (variant === 'temporary') {
      onClose();
    }
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path;
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  // Merge dynamic items into navigation config
  const enrichedConfig = useMemo(() => {
    return navigationConfig.map((group) => {
      if (group.id === 'receipts' && currentReceipt) {
        return {
          ...group,
          items: [
            ...group.items,
            {
              id: 'current-receipt',
              label: `Receipt ${currentReceipt.id.substring(0, 8)}...`,
              path: currentReceipt.path,
              icon: ReceiptLongIcon,
              dynamic: true,
            },
          ],
        };
      }
      return group;
    });
  }, [currentReceipt]);

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: variant === 'permanent' ? drawerWidth : undefined,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          top: variant === 'permanent' ? '64px' : 0,
          height: variant === 'permanent' ? 'calc(100% - 64px)' : '100%',
        },
      }}
    >
      {/* Close button for mobile */}
      {variant === 'temporary' && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={onClose}>
            <ChevronLeft />
          </IconButton>
        </Box>
      )}

      {/* Navigation Groups */}
      <List>
        {enrichedConfig.map((group: NavGroup) => (
          <Box key={group.id}>
            <ListItemButton onClick={() => toggleGroup(group.id)}>
              <ListItemText
                primary={group.label}
                primaryTypographyProps={{
                  fontWeight: 700,
                  color: 'text.primary',
                  variant: 'subtitle1',
                }}
              />
              {expandedGroups.includes(group.id) ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>

            <Collapse in={expandedGroups.includes(group.id)} timeout="auto">
              <List component="div" disablePadding>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <ListItemButton
                      key={item.id}
                      selected={active}
                      sx={{
                        pl: 4,
                      }}
                      onClick={() => item.path && handleNavigate(item.path)}
                    >
                      <ListItemIcon sx={{ color: active ? 'primary.main' : 'inherit', minWidth: 40 }}>
                        <Icon />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontWeight: active ? 600 : 500,
                          color: active ? 'primary.main' : 'text.primary',
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
            <Divider />
          </Box>
        ))}
      </List>
    </Drawer>
  );
};
